import './BasketItem.css';
import React, {useEffect, useMemo, useState} from "react";
import {useBasket} from "../../../context/BasketContext/BasketContext.jsx";
import {removeFromCart, updateCartItemQuantity} from "../../../CartHandler.js";
import {useNotification} from "../../../context/NotificationContext/NotificationContext.jsx";
import {checkStock} from "../../../ProductsHandler.js";
import {useMediaQuery} from "react-responsive"; // ← add this

export default function BasketItem({item}) {
    const [amount, setAmount] = useState(item.quantity);
    const [maxQty, setMaxQty] = useState(null); // number | null | 0
    const {bumpBasketVersion} = useBasket();
    const {setDisplayNotification} = useNotification();
    const isMobile = useMediaQuery({maxWidth: '768px'});

    // Try common shapes to find the variant GID the line refers to
    const resolveVariantId = (line) => {
        // Typical places people store it:
        return (
            line?.item?.variantId ||
            line?.item?.variantID ||
            line?.item?.variant?.id ||
            line?.variantId ||
            line?.variant?.id ||
            null
        );
    };

    // Fetch stock once for this line's variant (and when the variant changes)
    useEffect(() => {
        let active = true;
        const variantId = resolveVariantId(item);
        (async () => {
            if (!variantId) {
                setMaxQty(null);
                return;
            }
            try {
                const qty = await checkStock(variantId);
                if (!active) return;
                // qty: number = exact, null = in stock but unknown (no scope), 0 = OOS
                setMaxQty(qty);
                if (typeof qty === "number" && qty >= 0 && amount > qty) {
                    // clamp down if they had more than available
                    setAmount(Math.max(1, qty));
                }
            } catch {
                if (active) setMaxQty(null);
            }
        })();
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item]);

    // push quantity changes to Shopify
    useEffect(() => {
        const updateQuantity = async () => {
            await updateCartItemQuantity(item.item.id, amount);
            bumpBasketVersion();
        };
        updateQuantity();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [amount]);

    // UI guards
    const atMax = useMemo(() => (
        typeof maxQty === "number" && maxQty >= 0 && amount >= maxQty
    ), [amount, maxQty]);

    const plusDisabled = (maxQty === 0) || atMax; // 0 = OOS; atMax = reached ceiling

    return (!isMobile ?
        <div className="basket-item">
            <div className="basket-item-img">
                <img loading="lazy" src={item.item.img} alt={item.item.alt || item.item.name || 'Produkt'}/>

            </div>

            <div className="basket-item-name">{item.item.name}</div>

            <div className="basket-item-options">
                {item.item.options.length > 0 && item.item.options[0].value !== "Default Title" && item.item.options.map((option, index) => {
                    if (option.name === "Kolor") {
                        return <span className='basket-item-option' key={index}>{option.name}: <div
                            className='basket-item-color' style={{backgroundColor: option.value}}></div></span>
                    }
                    return <span className='basket-item-option' key={index}>{option.name}: {option.value}</span>
                })}
            </div>

            <div className="basket-item-price">{(item.item.price).toFixed(2)} zł</div>
            <div className="basket-item-total-price">
                {(amount * item.item.price).toFixed(2)} zł
            </div>

            <div className='basket-item-quantity'>
                <div
                    id='basket-item-amount-substract-bttn'
                    onClick={() => handleAmountChange(null, 'substraction', amount, setAmount)}
                    className='basket-item-amount-bttn'
                >
                    -
                </div>

                <input
                    type='number'
                    id='basket-item-amount-input'
                    value={amount}
                    min={1}
                    onChange={(e) => {
                        const raw = parseInt(e.target.value || "1", 10);
                        const next = Number.isFinite(raw) ? Math.max(1, raw) : 1;
                        // clamp only when we know the exact max
                        if (typeof maxQty === "number" && maxQty >= 0) {
                            setAmount(Math.min(next, Math.max(1, maxQty)));
                        } else {
                            setAmount(next);
                        }
                    }}
                />

                <div
                    id='basket-item-amount-add-bttn'
                    onClick={() => {
                        if (!plusDisabled) handleAmountChange(null, 'addition', amount, setAmount);
                    }}
                    className={`basket-item-amount-bttn ${plusDisabled ? 'amount-bttn-disabled' : ''}`}
                    aria-disabled={plusDisabled}
                    title={typeof maxQty === "number" ? `Maksymalna ilość: ${maxQty}` : undefined}
                >
                    +
                </div>
            </div>

            <div
                className="basket-item-remove-bttn"
                onClick={async () => {
                    await removeFromCart(item.item.id);
                    bumpBasketVersion();
                    setDisplayNotification({
                        type: 'message',
                        message: `Produkt${item.quantity > 0 ? 'y' : ''} został pomyślnie usunięty z koszyka!`
                    });
                }}
            >
                <img loading="lazy" src='/images/trash-icon.webp' alt=""/>

            </div>
        </div> :
        <div className="basket-item">
            <div className='basket-item-top'>
                <div className="basket-item-img">
                    <img loading="lazy" src={item.item.img} alt={item.item.alt || item.item.name || 'Produkt'}/>

                </div>

                <div className="basket-item-name-container">
                    <span className='basket-item-name'>{item.item.name}</span>
                    <span className='basket-item-variants'>
                       {item.item.options.length > 0 && item.item.options[0].value !== "Default Title" && item.item.options.map((option, index) => {
                           if (option.name === "Kolor") {
                               return <span className='basket-item-option' key={index}>{option.name}: <div
                                   className='basket-item-color' style={{backgroundColor: option.value}}></div></span>
                           }
                           return <span className='basket-item-option' key={index}>{option.name}: {option.value}</span>
                       })}
                </span>
                </div>
                <div
                    className="basket-item-remove-bttn"
                    onClick={async () => {
                        await removeFromCart(item.item.id);
                        bumpBasketVersion();
                        setDisplayNotification({
                            type: 'message',
                            message: `Produkt${item.quantity > 0 ? 'y' : ''} został pomyślnie usunięty z koszyka!`
                        });
                    }}
                >
                    <img loading="lazy" src='/images/trash-icon.webp' alt=""/>

                </div>
            </div>
            <div className="basket-item-bottom">

                {/*<div className="basket-item-options">*/}

                {/*</div>*/}

                <div className='basket-item-labels'>
                <span class='basket-item-labels-label' id='basket-item-label-price'>
                    Cena:
                </span>
                    <span class='basket-item-labels-label' id='basket-item-label-amount'>
                    Ilość:
                </span>
                    <span class='basket-item-labels-label' id='basket-item-label-total'>
                    Razem:
                </span>
                </div>
                <div className="basket-item-values">
                    <div className="basket-item-price">{(item.item.price).toFixed(2)} zł</div>

                    <div className='basket-item-quantity'>
                        <div
                            id='basket-item-amount-substract-bttn'
                            onClick={() => handleAmountChange(null, 'substraction', amount, setAmount)}
                            className='basket-item-amount-bttn'
                        >
                            -
                        </div>

                        <input
                            type='number'
                            id='basket-item-amount-input'
                            value={amount}
                            min={1}
                            onChange={(e) => {
                                const raw = parseInt(e.target.value || "1", 10);
                                const next = Number.isFinite(raw) ? Math.max(1, raw) : 1;
                                // clamp only when we know the exact max
                                if (typeof maxQty === "number" && maxQty >= 0) {
                                    setAmount(Math.min(next, Math.max(1, maxQty)));
                                } else {
                                    setAmount(next);
                                }
                            }}
                        />

                        <div
                            id='basket-item-amount-add-bttn'
                            onClick={() => {
                                if (!plusDisabled) handleAmountChange(null, 'addition', amount, setAmount);
                            }}
                            className={`basket-item-amount-bttn ${plusDisabled ? 'amount-bttn-disabled' : ''}`}
                            aria-disabled={plusDisabled}
                            title={typeof maxQty === "number" ? `Maksymalna ilość: ${maxQty}` : undefined}
                        >
                            +
                        </div>
                    </div>

                    <div className="basket-item-total-price">
                        {(amount * item.item.price).toFixed(2)} zł
                    </div>
                </div>


            </div>

        </div>)

}

// eslint-disable-next-line react-refresh/only-export-components
export const handleAmountChange = (value, operation, amount, setAmount) => {
    if (operation === "addition") {
        amount + 1 <= 99 ? setAmount(amount + 1) : null;
    } else if (operation === 'substraction') {
        amount - 1 >= 1 ? setAmount(amount - 1) : null;
    } else {
        if (value <= 1) setAmount(1);
        else if (value >= 99) setAmount(99);
        else setAmount(value);
    }
};
